import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { MessageCircle, Clock, Shield, DollarSign, Car, MapPin, Menu, X } from 'lucide-react';
import { mockData } from '../mockData';

const Home = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAllCars, setShowAllCars] = useState(false);

  const filteredCars = selectedCategory === 'all' 
    ? mockData.cars 
    : mockData.cars.filter(car => car.category === selectedCategory);

  // Show only first 6 cars unless "View All" is clicked
  const displayedCars = showAllCars ? filteredCars : filteredCars.slice(0, 6);

  const openWhatsApp = () => {
    const message = encodeURIComponent("Hi there! I'm looking for a car. What's available?");
    window.open(`https://wa.me/${mockData.whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold tracking-tight text-gray-900">
              drive<span className="text-green-500">ez</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#fleet" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Fleet</a>
              <a href="#why-us" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Why Us</a>
              <a href="#about" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">About</a>
              <a href="#location" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Location</a>
              <a href="#faq" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">FAQ</a>
              <Button onClick={openWhatsApp} className="bg-gray-900 hover:bg-gray-800 text-white">
                <MessageCircle className="w-4 h-4 mr-2" />
                Contact
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pt-4 pb-2 space-y-3">
              <a 
                href="#fleet" 
                className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Fleet
              </a>
              <a 
                href="#why-us" 
                className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Why Us
              </a>
              <a 
                href="#about" 
                className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </a>
              <a 
                href="#location" 
                className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Location
              </a>
              <a 
                href="#faq" 
                className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </a>
              <Button 
                onClick={() => {
                  openWhatsApp();
                  setMobileMenuOpen(false);
                }} 
                className="w-full bg-gray-900 hover:bg-gray-800 text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Contact
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-6xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-tight">
                Car rental,
                <span className="block text-green-500">made simple</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
                Reliable cars. Fair prices. No hidden fees.
                <br />Perfect for students and newcomers to Perth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={openWhatsApp} size="lg" className="bg-gray-900 hover:bg-gray-800 text-white text-base px-8 py-6">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Book on WhatsApp
                </Button>
                <Button size="lg" variant="outline" className="text-base px-8 py-6" onClick={() => document.getElementById('fleet').scrollIntoView({ behavior: 'smooth' })}>
                  View Fleet
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_driveez-rentals/artifacts/ujliiqst_B687859C-DAEF-44AE-8272-5E839B6C7200.PNG" 
                alt="Car keys handover with Perth skyline" 
                loading="eager"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badge */}
      <section className="py-4 px-6 border-b border-gray-100" data-testid="trust-badge">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-500" />
            <span>Licensed Motor Dealer <strong className="text-gray-700">MD 32367</strong></span>
          </div>
          <span className="hidden sm:inline text-gray-300">|</span>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-500" />
            <span>Fully Insured Vehicles</span>
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section id="fleet" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Our Fleet</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose from our diverse range of well-maintained vehicles
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex justify-center gap-3 mb-16">
            {['all', 'economy', 'suv'].map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? 'bg-gray-900 text-white' : ''}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Button>
            ))}
          </div>

          {/* Car Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedCars.map((car) => (
              <Card key={car.id} className="group hover:shadow-xl transition-all duration-300 border-gray-200 overflow-hidden">
                <div className="relative overflow-hidden">
                  <img 
                    src={car.image} 
                    alt={car.name} 
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-semibold capitalize">
                    {car.category}
                  </div>
                </div>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{car.name}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                    <span className="bg-gray-100 px-2 py-1 rounded">{car.transmission}</span>
                    <span className="bg-gray-100 px-2 py-1 rounded">{car.seats} seats</span>
                    {car.features.map((feature, idx) => (
                      <span key={idx} className="bg-gray-100 px-2 py-1 rounded">{feature}</span>
                    ))}
                  </div>
                  <div className="pt-4 border-t">
                    <Button 
                      onClick={openWhatsApp} 
                      size="sm" 
                      className="w-full bg-gray-900 hover:bg-gray-800"
                    >
                      Enquire
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* View All Cars Button */}
          {!showAllCars && filteredCars.length > 6 && (
            <div className="text-center mt-12">
              <Button 
                onClick={() => setShowAllCars(true)}
                variant="outline"
                size="lg"
                className="px-8"
              >
                View All Cars ({filteredCars.length})
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Choose drive<span className="text-green-500">ez</span></h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple. Transparent. Stress-free.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.features.map((feature, index) => {
              const Icon = feature.icon === 'DollarSign' ? DollarSign : 
                          feature.icon === 'Car' ? Car : 
                          feature.icon === 'Shield' ? Shield : Clock;
              return (
                <div key={index} className="text-center space-y-4 p-6 rounded-2xl hover:bg-gray-50 transition-colors">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 text-white rounded-2xl">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-6">
            <h2 className="text-5xl font-bold text-gray-900">About drive<span className="text-green-500">ez</span></h2>
            <div className="space-y-4 text-lg text-gray-600 leading-relaxed max-w-3xl">
              <p>
                Hey! We're a small car rental in Perth, run by people who understand what it's like to be new here.
              </p>
              <p>
                No complicated contracts. No corporate nonsense. Just reliable cars and straight answers.
                If you have questions, message us. We'll reply like humans, not robots.
              </p>
              <p>
                New to Perth or Australia? We get it. We're here to make your life easier, not harder.
              </p>
            </div>
          </div>
        </div>
      </section>
      
         {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-xl text-gray-600">
              Real reviews from students and newcomers
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                name: 'Igor',
                text: "Awesome experience with Driveez Car! Everything went perfectly, and Team was super helpful and easy to deal with. Would recommend 100%."
              },
              {
                name: 'Pedro',
                text: "I rented the car last week when I had just arrived in Perth and everything went really well! Team was very friendly and helpful from the beginning. Everything was great."
              }
            ].map((review, index) => (
              <Card key={index} className="border-gray-200" data-testid={`review-card-${index}`}>
                <CardContent className="p-8 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center text-white font-bold text-lg">
                      {review.name[0]}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">{review.name}</h4>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-4 h-4 fill-yellow-400" viewBox="0 0 20 20">
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-lg italic">"{review.text}"</p>
                </CardContent>
              </Card>
            ))}

            <div className="text-center pt-6 space-y-3">
              <a 
                href="https://maps.app.goo.gl/p4bUzCnoTWbXZAxJ7" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
                data-testid="google-reviews-link"
              >
                <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white">
                  See All Reviews on Google
                </Button>
              </a>
              <p className="text-sm text-gray-500">
                Leave us a review and help others find simple car rental
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Location Section */}
      <section id="location" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">We're in Perth</h2>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="border-gray-200">
              <CardContent className="p-8 text-center space-y-6">
                <div className="inline-flex items-center justify-center mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_driveez-rentals/artifacts/esgouq00_C825AA63-C8D5-41A3-A59E-F8010114CDC5.PNG"
                    alt="Western Australia Map"
                    className="w-32 h-32 object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Perth, Western Australia</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    North of the river. Flexible pickup locations across northern suburbs.
                  </p>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-3">We speak your language</p>
                  <div className="flex items-center justify-center gap-3 text-sm text-gray-600">
                    <span>🇦🇺 English</span>
                    <span>•</span>
                    <span>🇧🇷 Portuguese</span>
                    <span>•</span>
                    <span>🇪🇸 Spanish</span>
                  </div>
                </div>
                <Button onClick={openWhatsApp} size="lg" className="w-full bg-gray-900 hover:bg-gray-800 text-white">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Book via WhatsApp
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Common Questions</h2>
            <p className="text-xl text-gray-600">
              Everything you need to know
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white border border-gray-200 rounded-xl px-6">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
                Who is Driveez for?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                Driveez is designed for international students, new arrivals, and anyone looking for a simple, affordable car rental in Australia — without corporate complexity.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white border border-gray-200 rounded-xl px-6">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
                What do I need to rent a car?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                A valid driver's licence and basic identification. We keep requirements simple, especially for newcomers. If unsure, just reach out.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white border border-gray-200 rounded-xl px-6">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
                Are there any hidden fees?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                No. Pricing is clear and agreed upfront. No surprises or unexpected charges.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white border border-gray-200 rounded-xl px-6">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
                Are the cars reliable and well maintained?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                Yes. All vehicles are regularly serviced, cleaned, and checked. Safety and reliability come first.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white border border-gray-200 rounded-xl px-6">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
                How long can I rent a car for?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                We offer flexible rentals, including weekly and longer-term options, depending on your needs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-white border border-gray-200 rounded-xl px-6">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
                What makes Driveez different from big rental companies?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                Driveez is more flexible, more transparent, and more human. No unnecessary rules or complicated contracts.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="bg-white border border-gray-200 rounded-xl px-6">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
                Is insurance included?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                Yes. All rentals include insurance for peace of mind. Full details are explained clearly before you rent.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <Button onClick={() => openWhatsApp()} size="lg" className="bg-gray-900 hover:bg-gray-800 text-white">
              <MessageCircle className="w-5 h-5 mr-2" />
              Ask on WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-5xl font-bold text-gray-900">Need a car?</h2>
          <p className="text-xl text-gray-600">
            Message us. We'll sort you out.
          </p>
          <Button onClick={openWhatsApp} size="lg" className="bg-gray-900 hover:bg-gray-800 text-white text-lg px-12 py-7">
            <MessageCircle className="w-6 h-6 mr-2" />
            Book Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div className="space-y-4">
              <div className="text-2xl font-bold">drive<span className="text-green-500">ez</span></div>
              <p className="text-gray-400 text-sm">
                Simple car rental in Perth. Made for students and newcomers.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#fleet" className="hover:text-white transition-colors">Our Fleet</a></li>
                <li><a href="#why-us" className="hover:text-white transition-colors">Why Us</a></li>
                <li><a href="#about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Reviews</a></li>
                <li><a href="#location" className="hover:text-white transition-colors">Location</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company Details</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Driveez Car Rental</li>
                <li>ABN: 17 689 326 976</li>
                <li>Motor Dealer Licence (MD): MD 32367</li>
                <li>Location: Perth, Western Australia</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>&copy; 2025 driveez. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 z-50 group animate-pulse-slow"
        aria-label="Contact us on WhatsApp"
        style={{ animation: 'pulse 2s infinite' }}
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
          Message us on WhatsApp
        </span>
      </button>
    </div>
  );
};

export default Home;
